import React, { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import { Button, TextField, Tooltip, Typography } from "@material-ui/core";
import StyledPanel from "../StyledPanel";
import { useStyles } from "./style";
import { generateUUID } from "../../Rate";
import DeleteIcon from "@material-ui/icons/Delete";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import { useTheme } from "@material-ui/core/styles";
import clsx from "clsx";
import { isUndefined, isEmpty, isEqual } from "lodash";
import apiClient from "../../../auth/apiClient";
import ContractService from "../../../services/ContractService";
import { useConfirmationDialog } from "../../../hooks/useConfirmationDialog";
import { useForm, Controller, FormProvider } from "react-hook-form";
import * as Yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { AddressSubformSchema } from "../../Forms/ContactSubform/AddressSubform";
import { EmailSubformSchema } from "../../Forms/ContactSubform/EmailSubform";
import { PhoneSubformSchema } from "../../Forms/ContactSubform/PhoneSubform";
import useHasPermissions from "../../../hooks/useHasPermissions";
import useCurrentFacility from "../../../hooks/useCurrentFacility";
import { useEnqueueSnackbar } from "../../../hooks/useEnqueueSnackbar";
import ContactSubform from "../../Forms/ContactSubform";
import { useSelector } from "react-redux";
import ViewNotesButton from "../../Notes/ViewNotesButton/ViewNotesButton";
import PinnedNote from "../../Notes/PinnedNote/PinnedNote";
import NotesProvider from "../../../providers/NotesProvider";
import {useFlags} from "launchdarkly-react-client-sdk";
import { NoteType } from "../../../constants/NoteCategoryEnum";
import useNotesPermissions from "../../../hooks/useNotesPermissions";
const contractService = new ContractService(apiClient);

// Req 2.8: single source of truth for the balance guardrail tooltip wording.
const BALANCE_TOOLTIP =
  "To delete this access holder, their balance must be $0.";

// Req 3.1: confirmation message shown when deleting a ONEpark access holder.
const ONEPARK_DELETE_MESSAGE =
  "This will delete the access holder and their ONEpark login. To confirm the deletion of this access holder, please enter this access holder's first name in the input box below.";

// Req 3.6: default confirmation message for non-ONEpark access holders.
const DEFAULT_DELETE_MESSAGE =
  "To confirm the deletion of this access holder, please enter this access holder's first name in the input box below.";

const AccessHolderPanel = ({
  className,
  accessHolderID,
  onDelete,
  onChange,
  fundBalanceOverride,
}) => {
  const classes = useStyles();
  const { facilityID } = useCurrentFacility();
  const [accessHolder, setAccessHolder] = useState({
    accessHolderID,
  });
  // Guardrail state (Req 2): null = balance not yet known
  const [fundBalance, setFundBalance] = useState(null);
  const [balanceLoading, setBalanceLoading] = useState(false);
  const [balanceError, setBalanceError] = useState(false);
  const theme = useTheme();
  const small = useMediaQuery(theme.breakpoints.down("sm"));
  const { textConfirmation } = useConfirmationDialog();
  const { hasPermissions } = useHasPermissions();
  const accessHolderDelete = hasPermissions(["accessholders.delete"]);
  const accessHolderEdit = hasPermissions(["accessholders.edit"]);
  const accessHolderAdd = hasPermissions(["accessholders.add"]);
  const canEditOneParkEmail = accessHolderEdit;
  const enqueue = useEnqueueSnackbar();
  const { notesField } = useFlags();
  const {canView} = useNotesPermissions();
  const facilityGroupID = useSelector((state) => state.entityScope?.facilityGroupId);
  
  const AccessHolderSchema = Yup.object().shape({
    accountNumber: Yup.string().required("* Required"),
    company: Yup.string().nullable(),
    contactInfo: Yup.object().shape({
      firstName: Yup.string().required("* Required"),
      lastName: Yup.string(),
      addresses: Yup.array().of(AddressSubformSchema),
      emails: Yup.array().of(EmailSubformSchema),
      phoneNumbers: Yup.array().of(PhoneSubformSchema),
    }),
  });

  const defaultValues = {
    accountNumber: "",
    company: "",
    contactInfo: {
      firstName: "",
      lastName: "",
      addresses: [
        {
          type: "Home",
          addressLine1: "",
          addressLine2: "",
          countryID: "",
          city: "",
          subdivisionID: "",
          postalCode: "",
        },
      ],
      emails: [{ type: "Personal", emailAddress: "" }],
      phoneNumbers: [{ type: "Mobile", number: "" }],
    },
  };

  const methods = useForm({
    resolver: yupResolver(AccessHolderSchema),
    defaultValues: defaultValues,
    mode: "onChange",
  });

  const {
    handleSubmit,
    control,
    reset,
    setValue,
    trigger,
    formState: { isDirty, isSubmitting, errors, isValid },
  } = methods;

  useEffect(() => {
    if (!isUndefined(accessHolder.accessHolderID)) {
      fetchAccessHolder(facilityID, accessHolder.accessHolderID);
    } else {
      const accNumber = generateUUID();
      setAccessHolder((prev) => ({ ...prev, accountNumber: accNumber }));
      reset({ ...defaultValues, accountNumber: accNumber });
    }
  }, [accessHolder.accessHolderID]);

  const setFormValuesFromResponse = (data) => {
    const {
      accountNumber,
      company,
      contactInfo: {
        addresses,
        contactID,
        entityID,
        emails,
        firstName,
        lastName,
        phoneNumbers,
      },
    } = data;
    const formValues = {
      accountNumber: accountNumber,
      company: company,
      contactInfo: {
        addresses:
          addresses?.length > 0
            ? addresses
            : defaultValues.contactInfo.addresses,
        contactID: contactID,
        entityID: entityID,
        emails: emails?.length > 0 ? emails : defaultValues.contactInfo.emails,
        firstName: firstName,
        lastName: lastName,
        phoneNumbers:
          phoneNumbers?.length > 0
            ? phoneNumbers
            : defaultValues.contactInfo.phoneNumbers,
      },
    };
    reset(formValues);
  };

  const scopeAwareFacilityID = facilityGroupID || facilityID;

  const fetchFundBalance = useCallback(
    async (contractID) => {
      // Req 2.4: no contract/fund => treat as $0.00 (enabled)
      if (!contractID || !scopeAwareFacilityID) {
        setFundBalance(0);
        setBalanceError(false);
        setBalanceLoading(false);
        return;
      }
      setBalanceLoading(true); // Req 2.6: disabled while loading
      setBalanceError(false);
      try {
        const response = await contractService.getFundBalance(
          scopeAwareFacilityID,
          contractID
        );
        // Req 2.4: absent balance => 0; Req 2.5: use fund balance, not points
        setFundBalance(response?.data?.balance ?? 0);
      } catch {
        // Req 2.4 (matching the group-contract guardrail fix): an access holder
        // with no FPA fund row causes getFundBalance to 404/throw. That is a
        // "no fund" case => treat as exactly $0.00 (enabled), NOT a permanent
        // disabling error state. The guardrail only disables for a KNOWN
        // NON-ZERO balance, so resolve to 0 here.
        setFundBalance(0);
        setBalanceError(false);
      } finally {
        setBalanceLoading(false);
      }
    },
    [scopeAwareFacilityID]
  );

  const fetchAccessHolder = useCallback(
    async (facilityID, accessHolderID) => {
      let response;
      try {
        response = await contractService.getAccessHolder(
          facilityID,
          accessHolderID
        );
      } catch {
        enqueue("Failed to retrieve Access Holder", {
          variant: "error",
          tag: "accessHolderFetchError",
        });
        return;
      }
      onChange(response.data);
      setAccessHolder(response.data);
      if (isEmpty(response.data.contactInfo)) {
        // This case happens when the contact info for the access holder is undefined.  It is undefined because
        // the contactid column of the accessholder record in the db is null.  Set the 
        // contact info to default values(empty strings) so that the rest of the access holder record
        // will display on the ui.
        response.data.contactInfo = defaultValues.contactInfo;
      }   
      setFormValuesFromResponse(response.data);
      // Req 2.x: fetch the FPA fund balance for the guardrail once the AH resolves.
      // The response carries contractID (see AccessHolderQueries.GetAccessHolder).
      fetchFundBalance(response.data.contractID);
    },
    [facilityID, accessHolder.accessHolderID, fetchFundBalance]
  );
  const isOneParkUser =  accessHolder?.isOneParkUser
  const [emailChangeStatus, setEmailChangeStatus] = useState("none");
  const [pendingEmail, setPendingEmail] = useState("");
  const currentEmail = accessHolder?.contactInfo?.emails?.[0]?.emailAddress;

  const refreshOneParkEmailChangeStatus = useCallback(async () => {
    if (!isOneParkUser || isUndefined(accessHolder.accessHolderID)) return;
    const entityID = facilityGroupID || facilityID;
    try {
      const { data } = await contractService.getOneParkAccessHolderEmailChangeStatus(
        accessHolder.accessHolderID,
        entityID
      );
      setEmailChangeStatus(data?.emailChangeStatus || "none");
      setPendingEmail(data?.pendingEmail || "");
    } catch {
      // Status is non-critical; leave any prior value in place on failure.
    }
  }, [isOneParkUser, accessHolder.accessHolderID, facilityGroupID, facilityID]);

  // On load (and whenever the ONEpark/access-holder identity changes), pull the current pending status.
  useEffect(() => {
    refreshOneParkEmailChangeStatus();
  }, [refreshOneParkEmailChangeStatus]);

  const handleChangeOneParkEmail = useCallback(
    async (newEmail) => {
      const entityID = facilityGroupID || facilityID;
      const response = await contractService.changeOneParkAccessHolderEmail(
        accessHolder.accessHolderID,
        entityID,
        newEmail
      );
      await refreshOneParkEmailChangeStatus();
      return response;
    },
    [accessHolder.accessHolderID, facilityGroupID, facilityID, refreshOneParkEmailChangeStatus]
  );

  // Live-update path: when the sibling FlexibleParkingAccountPanel reports a new
  // balance (initial load OR an Edit Balance submission), lifted up through
  // AccessHolderForm as fundBalanceOverride, apply it to the guardrail state so
  // the Delete button enables/disables immediately without a page refresh.
  // A defined numeric override takes precedence over the panel's own fetched
  // balance and marks the balance as known (not loading, not errored).
  useEffect(() => {
    if (!isUndefined(fundBalanceOverride)) {
      setFundBalance(fundBalanceOverride);
      setBalanceLoading(false);
      setBalanceError(false);
    }
  }, [fundBalanceOverride]);

  // Guardrail predicate (Req 2): pure function of balance/loading/error only —
  // independent of isOneParkUser/pointsBalance (Req 2.3, 2.5).
  const isBalanceZero = fundBalance === 0; // exact-zero check (positive & negative both disabled, Req 2.1)
  const balanceKnown =
    fundBalance !== null && !balanceLoading && !balanceError;
  const deleteDisabledForBalance = !balanceKnown || !isBalanceZero; // Req 2.1, 2.2, 2.6, 2.7

  async function createOrUpdateAccessHolder(values) {
    const { contactInfo, accountNumber, company } = values;
    if (isUndefined(contactInfo)) return;
    if (isEqual(contactInfo.addresses, defaultValues.contactInfo.addresses)) {
      contactInfo.addresses = undefined;
    }
    if (isEqual(contactInfo.emails, defaultValues.contactInfo.emails)) {
      contactInfo.emails = undefined;
    }
    if (
      isEqual(contactInfo.phoneNumbers, defaultValues.contactInfo.phoneNumbers)
    ) {
      contactInfo.phoneNumbers = undefined;
    }
    contactInfo.firstName = contactInfo.firstName;
    contactInfo.lastName = contactInfo.lastName;
    contactInfo.entityID = facilityGroupID || facilityID;
    accessHolder.contactInfo = contactInfo;
    accessHolder.accountNumber = accountNumber;
    accessHolder.company = company;
    let response;
    if (isUndefined(accessHolder.accessHolderID)) {
      try {
        accessHolder.startDate = new Date();
        response = await contractService.createAccessHolder(accessHolder);
      } catch (err) {
        if (err?.response?.status === 409) {
          enqueue("Failed to create access holder. This account number is already in use.", {
            variant: "error",
            tag: "accessHolderAcctNumberConflict",
          });
          return;
        }
        enqueue("Failed to create access holder", {
          variant: "error",
          tag: "accessHolderCreateError",
        });
        return;
      }
    } else {
      try {
        response = await contractService.updateAccessHolder(accessHolder);
      } catch (err) {
        if (err?.response?.status === 409) {
          enqueue("Failed to update access holder. This account number is already in use.", {
            variant: "error",
            tag: "accessHolderAcctNumberConflict",
          });
          return;
        }
        enqueue("Failed to update access holder", {
          variant: "error",
          tag: "accessHolderUpdateError",
        });
        return;
      }
    }

    enqueue(
      `Successfully ${
        isUndefined(accessHolder.accessHolderID) ? "created" : "updated"
      } access holder`,
      {
        variant: "success",
      }
    );

    setAccessHolder((prev) => ({
      ...response.data,
      contactInfo: prev.contactInfo,
    }));
    onChange(response.data);
  }

  const handleDeleteAccessHolder = async () => {
    // Req 2.2: defensive guard — never start the delete flow while the
    // balance guardrail disables deletion (unknown/loading/errored/non-zero).
    if (deleteDisabledForBalance) return;

    const confirmed = await textConfirmation({
      title: "Access Holder Delete",
      // Req 3.1 / 3.6: ONEpark message when the AH is a ONEpark user, else the default.
      message: isOneParkUser ? ONEPARK_DELETE_MESSAGE : DEFAULT_DELETE_MESSAGE,
      textConfirmation: accessHolder.contactInfo.firstName,
    });
    if (confirmed) {
      try {
        await contractService.deleteAccessHolder(accessHolder.accessHolderID);
      } catch {
        enqueue("Failed to delete access holder", {
          variant: "error",
          tag: "accessHolderDeleteError",
        });
        return;
      }
      enqueue("Access Holder deleted successfully", {
        variant: "success",
      });
      setAccessHolder({ accountNumber: generateUUID() });
      onDelete();
    }
  };

  const shouldModificationBeDisabled = () => {
    if (isUndefined(accessHolder.accessHolderID)) {
      return !accessHolderAdd;
    } else {
      return !accessHolderEdit;
    }
  };

  const formatTextField = (e) => {
    const val = (e.target.value || "").replace(/\s+/gi, " ");
    setValue(e.target.name, val.trim());
    trigger(e.target.name);
  };

  return (
    <>
      <StyledPanel
        className={clsx("accessholder-panel", className)}
        headerContent={
          <div className={clsx("header", classes.header)}>
            <div className={clsx("header-left", classes.headerLeft)}>
              <Typography className={clsx("access-holder-info")}>
                Access Holder Info
              </Typography>
              {/* <ContractsDropdown
                className={classes.contractDropdown}
                variant="outlined"
              /> TO BE ADDED AFTER PHASE 1A*/}
            </div>
            <div className={clsx("button-header", classes.headerButtons)}>
              {accessHolderDelete && !isUndefined(accessHolder.accessHolderID) && (
                <Tooltip
                  title={!balanceKnown ? "" : !isBalanceZero ? BALANCE_TOOLTIP : ""}
                  arrow
                >
                  {/* span wrapper required so the tooltip receives hover events for a disabled button (MUI v4) */}
                  <span className={classes.deleteBtnWrapper}>
                    <Button
                      size="small"
                      className={clsx(
                        "delete-accessholder-btn",
                        classes.mobileBtn,
                        classes.deleteBtn
                      )}
                      startIcon={
                        small === true && (
                          <DeleteIcon className={clsx("delete-icon")} />
                        )
                      }
                      variant="contained"
                      color="secondary"
                      disabled={deleteDisabledForBalance}
                      onClick={handleDeleteAccessHolder}
                    >
                      {!small && "Delete Access Holder"}
                    </Button>
                  </span>
                </Tooltip>
              )}
            </div>
          </div>
        }
        cardContent={
          <div className={clsx("card-content", classes.contentRoot)}>
            <FormProvider {...methods}>
              <form
                onSubmit={handleSubmit(
                  async (data) => await createOrUpdateAccessHolder(data)
                )}
                noValidate
              >
                <div className={clsx("card-top", classes.top)}>
                  <Controller
                    name="accountNumber"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        className={clsx(
                          "accountNumber-field",
                          classes.accountNumberInput
                        )}
                        size="small"
                        label="Account #"
                        variant="outlined"
                        error={!!errors.accountNumber}
                        helperText={
                          errors.accountNumber && errors.accountNumber.message
                        }
                        InputProps={{
                          readOnly: Boolean(shouldModificationBeDisabled()),
                          "aria-readonly": Boolean(
                            shouldModificationBeDisabled()
                          ),
                          disabled: Boolean(shouldModificationBeDisabled()),
                        }}
                      />
                    )}
                  />
                  <Controller
                    name="company"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        className={clsx(
                          "company-field",
                          classes.accountNumberInput
                        )}
                        size="small"
                        label="Company"
                        variant="outlined"
                        onBlur={formatTextField}
                        error={!!errors.company}
                        helperText={errors.company && errors.company.message}
                        InputProps={{
                          readOnly: Boolean(shouldModificationBeDisabled()),
                          "aria-readonly": Boolean(
                            shouldModificationBeDisabled()
                          ),
                          disabled: Boolean(shouldModificationBeDisabled()),
                        }}
                      />
                    )}
                  />
                </div>

                <div
                  className={clsx(
                    "contact-form-container",
                    classes.root,
                    classes.contactForm
                  )}
                >
                  <ContactSubform
                    disabled={shouldModificationBeDisabled()}
                    requiredFields={["firstName"]}
                    isOneParkUser={isOneParkUser}
                    canEditOneParkEmail={canEditOneParkEmail}
                    emailChangeStatus={emailChangeStatus}
                    pendingEmail={pendingEmail}
                    currentEmail={currentEmail}
                    onChangeOneParkEmail={handleChangeOneParkEmail}
                  />
                  <div className={classes.buttonContainer}>
                    {(notesField && canView && accessHolder?.accessHolderID) && (
                      <NotesProvider noteTypeId={NoteType.AccessHolder} contextId={accessHolder.accessHolderID} entityId={facilityID}>
                        <div className={classes.notesGroup}>
                          <PinnedNote />
                          <div className={classes.viewButtonWrpr}>
                            <ViewNotesButton />
                          </div>
                        </div>
                      </NotesProvider>
                    )}
                      {!shouldModificationBeDisabled() && (
                        <Button
                          className={clsx(
                            "accessholder-submit-btn",
                            classes.submitBtn
                          )}
                          data-testid="accessholder-submit-btn-test"
                          color="primary"
                          variant="contained"
                          type="submit"
                          disabled={
                            !isDirty || !isEmpty(errors) || !isValid || isSubmitting
                          }
                        >
                          Submit
                        </Button>
                      )}  
                  </div>
                </div>
              </form>
            </FormProvider>
          </div>
        }
      />
    </>
  );
};

AccessHolderPanel.defaultProps = {
  onDelete: () => {},
  onChange: () => {},
  fundBalanceOverride: undefined,
};

AccessHolderPanel.propTypes = {
  className: PropTypes.string,
  accessHolderID: PropTypes.string,
  onDelete: PropTypes.func,
  onChange: PropTypes.func,
  fundBalanceOverride: PropTypes.number,
};

export default AccessHolderPanel;
